import { urqlGql } from '@pypestream/api-services';
import {
  SetProfilePictureParams,
  UploadResponse,
  dismissToasts,
} from '@pypestream/design-system';

import { tableStore } from '../stores/table-store';
import { sendUserMessage } from '../xstate/app.xstate-utils';
import { normalizeCompilerErrors } from './validate-version';
import { abortWhenIdle } from './csv-request-timeout';

type UploadFileProps = Omit<SetProfilePictureParams, 'file'> & {
  url: string;
  versionId: string;
  versionIdLegacy: string;
  file: File | FileList | null;
};

export type BotCompilerErrorMsg = {
  error_description: string;
  field_entry?: string | null;
  field_name?: string | null;
};

export type BotCompilerError = {
  row_num: number | null;
  node_num: number | null;
  cell_num: number | null;
  err_msgs: BotCompilerErrorMsg[];
  column?: string | null;
  node_index: number | null;
  cell_index: number | null;
  // Stable node id (from the agent-validator diagnostic's data.id). The most
  // reliable address to a cell — survives a blank node number (PSTUDIO-3991).
  node_id?: string | null;
  code?: string;
  severity?: string;
};

export type BotCompilerErrors = {
  errors?: BotCompilerError[];
};

export const validateCsv = async ({
  url,
  file,
  versionId,
  versionIdLegacy,
  load,
  progress,
  abort,
  onChange,
}: UploadFileProps): Promise<{
  errors: string[];
  botCompilerErrors: BotCompilerError[];
  /** The request failed or timed out: the empty lists are NOT a verdict. */
  networkError?: true;
}> => {
  return new Promise((resolve, _reject) => {
    const request = new XMLHttpRequest();
    request.withCredentials = true;

    const errors: string[] = [];
    let botCompilerErrors: BotCompilerError[] = [];

    request.upload.onprogress = (e) =>
      progress?.(e.lengthComputable, e.loaded, e.total);

    request.onload = () => {
      const lastServerSync = new Date().toISOString();

      if (request.status >= 200 && request.status < 300) {
        const result: UploadResponse = JSON.parse(request.response);

        load?.(request.responseText);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onChange?.({ data: result.data });

        dismissToasts('validateCsv-invalid');

        urqlGql
          .updateProStudioVersionValidation({
            solutionVersionId: versionId,
            projectProStudioSettingId: tableStore.projectProStudioSettingId,
            lastServerValidation: lastServerSync,
            lastServerValidationMessages: [],
          })
          .then(() => {
            tableStore.lastServerValidation = new Date(lastServerSync);
            tableStore.lastServerValidationMessages = [];
            resolve({ errors, botCompilerErrors: [] });
          });
      } else {
        try {
          const response = JSON.parse(request.response) as BotCompilerErrors;

          if (response?.errors) {
            botCompilerErrors = normalizeCompilerErrors(response.errors);
            for (const e of botCompilerErrors) {
              const msg =
                e.err_msgs?.map((m) => m.error_description).join('\n') || '';
              errors.push(e.column ? `${e.column} column: ${msg}` : msg);
            }
          }
        } catch (error) {
          const errorMessage = 'Failed to parse validate CSV response: ';
          console.error(errorMessage, error);
          botCompilerErrors = [
            {
              row_num: null,
              node_num: null,
              cell_num: null,
              node_index: null,
              cell_index: null,
              code: undefined,
              severity: undefined,
              err_msgs: [
                {
                  error_description:
                    error instanceof Error
                      ? `${errorMessage}${error.message}`
                      : errorMessage,
                  field_entry: null,
                  field_name: null,
                },
              ],
            },
          ];
        }

        urqlGql
          .updateProStudioVersionValidation({
            solutionVersionId: versionId,
            projectProStudioSettingId: tableStore.projectProStudioSettingId,
            lastServerValidation: lastServerSync,
            lastServerValidationMessages: errors,
          })
          .then(() => {
            tableStore.lastServerValidation = new Date(lastServerSync);
            tableStore.lastServerValidationMessages = errors;
            resolve({ errors, botCompilerErrors });
          });
      }
    };

    const onNetworkError = () => {
      sendUserMessage({
        text: 'Network or server error during validation.',
        type: 'error',
        autoClose: 3000,
      });
      resolve({ errors, botCompilerErrors: [], networkError: true });
    };
    request.onerror = onNetworkError;

    const formData = new FormData();
    const filesArray = file instanceof FileList ? Array.from(file) : [file];
    filesArray.forEach((f) => f && formData.append('templateFile', f));

    // A request that stops answering is a network error, not a pass that
    // never ends (PSTUDIO-4242).
    abortWhenIdle(request, onNetworkError);
    // open() only after every upload listener is registered — some browsers
    // never fire upload progress for listeners added later (MDN), and then the
    // idle timer would abort a live upload.
    request.open('PUT', url);
    request.setRequestHeader('version_id', versionIdLegacy);
    request.send(file ? formData : undefined);
  });
};
